
import { Component, Inject, Vue, Watch } from "vue-property-decorator";
import { Container } from "inversify";
import { AuthStore } from "../../../../../common/src/stores/AuthStore";
import { OrganizationStore } from "../../../stores/organization/OrganizationStore";
import LoadingOverlay from "../../../../../common/src/components/LoadingOverlay.vue";
import { OrganizationItem } from "@gsx/common/src/types/http/dashboard/organization";
import { UserEditFavoriteStore } from "../../../stores/user/UserEditFavoriteStore";
import {
    formatPhysicalAddressWithOrganization,
    formatPointOfContactAddress,
} from "@gsx/common/src/utils/organizationUtils";
import { OrganizationProjectsCollectionStore } from "../../../stores/organization/resource/OrganizationProjectsCollectionStore";
import { handleError } from "../../../../../common/src/utility/errorUtils";
import { QuiltyInsightsListStore } from "../../../stores/quiltyInsights/QuiltyInsightsListStore";
import { LMap, LMarker, LTileLayer } from "vue2-leaflet";
import { QuiltyResourcesListStore } from "../../../stores/quiltyResources/QuiltyResourcesListStore";

@Component({
    components: {
        LoadingOverlay,
        LMap,
        LTileLayer,
        LMarker,
    },
})
export default class OrganizationSidebar extends Vue {
    @Inject("container") readonly container!: Container;
    readonly authStore: AuthStore = this.container.get(AuthStore);
    readonly organizationStore: OrganizationStore = this.container.get(OrganizationStore);
    readonly organizationProjectsStore: OrganizationProjectsCollectionStore = this.container.get(
        OrganizationProjectsCollectionStore,
    );
    readonly quiltyInsightsListStore: QuiltyInsightsListStore =
        this.container.get(QuiltyInsightsListStore);
    readonly userEditFavoriteStore: UserEditFavoriteStore =
        this.container.get(UserEditFavoriteStore);
    readonly quiltyResourcesListStore: QuiltyResourcesListStore =
        this.container.get(QuiltyResourcesListStore);

    socialMedias = [
        "Facebook",
        "Flickr",
        "Instagram",
        "LinkedIn",
        "Livestream",
        "Twitter",
        "YouTube",
    ];

    created() {
        if (this.quiltyInsightsListStore.organizationId !== this.organizationId) {
            this.quiltyInsightsListStore.organizationId = this.organizationId;
            this.quiltyInsightsListStore.reload().catch(handleError);
        }
    }

    get organizationId(): string {
        return this.$route.params.organization;
    }

    get organization(): OrganizationItem | null {
        return this.organizationStore.item;
    }

    get organizationUrl() {
        return `/organizations/${this.organizationId}`;
    }

    get addressLabel(): string {
        if (!this.organization) {
            return "";
        }

        const addressDetails = [this.organization.city, this.organization.country];

        return addressDetails.join(", ");
    }

    get physicalAddress(): string {
        return this.organization ? formatPhysicalAddressWithOrganization(this.organization) : "";
    }

    get pointOfContactAddress(): string[] {
        return this.organization ? formatPointOfContactAddress(this.organization) : [];
    }

    get quiltyResources() {
        return this.quiltyResourcesListStore.items || [];
    }

    get formattedWebAddress() {
        const webAddr = this.organization?.webAddr;
        if (webAddr && webAddr.startsWith("www")) {
            return `https://${webAddr}`;
        }
        return webAddr;
    }

    get isValidUrl() {
        const webAddr = this.organization?.webAddr;
        if (!webAddr) {
            return false;
        }
        const regex = /^(http|https|www)/i;
        return regex.test(webAddr);
    }

    @Watch("organizationId")
    load() {
        this.organizationProjectsStore.load(this.organizationId).catch(handleError);

        this.quiltyInsightsListStore.organizationId = this.organizationId;
        this.quiltyInsightsListStore.reload().catch(handleError);
    }
}
